import {
  defaultDropAnimationSideEffects,
  DndContext,
  DragEndEvent,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Button, Modal } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import Loader from '@/components/Atoms/Loader/Loader'
import { PrioritySaveBar } from '@/components/PrioritySortShared/PrioritySaveBar/PrioritySaveBar'
import {
  PrioritySortRow,
  SortablePriorityRow,
} from '@/components/PrioritySortShared/PrioritySortRow/PrioritySortRow'
import cn from '@/components/PrioritySortShared/PrioritySortShared.module.scss'
import {
  getComparableState,
  getPrioritizedCountAfterReorder,
  hasComparableStateChanged,
} from '@/components/PrioritySortShared/priorityState'
import { MAX_PRIORITY } from '@/constants'
import { usePriorityHistory } from '@/hooks/ui/usePriorityHistory'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { hasRoleFromGroup } from '@/utils/auth/roleGroups'

import BrandAliasesModal from './BrandAliasesModal/BrandAliasesModal'

interface Brand {
  id: string
  code: number
  description: string
  priority?: number
}

const BrandsPrioritySort: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [originalIds, setOriginalIds] = useState<string[]>([])
  const [originalPriorityMap, setOriginalPriorityMap] = useState<
    Record<string, number | undefined>
  >({})
  const [brandsMap, setBrandsMap] = useState<Record<string, Brand>>({})
  const [activeId, setActiveId] = useState<string | null>(null)
  const [aliasesBrand, setAliasesBrand] = useState<Brand | null>(null)
  const canEditAliases = useUserInfoStore((state) =>
    hasRoleFromGroup(state.tokenRoles, 'CATALOG_EDITORS')
  )
  const [modal, contextHolder] = Modal.useModal()
  const { selectedOrganisation } = useUserInfoStore()

  const {
    state: orderedIds,
    push,
    undo,
    redo,
    reset,
    canUndo,
    canRedo,
  } = usePriorityHistory<string[]>([])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const fetchBrands = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.get('/brands')
      const brands: Brand[] = response.data.brands || response.data || []
      const ids = brands.map((b) => b.id)
      const map = Object.fromEntries(brands.map((b) => [b.id, b]))
      const priorities = Object.fromEntries(
        brands.map((b) => [b.id, b.priority])
      )
      reset(ids)
      setOriginalIds([...ids])
      setOriginalPriorityMap(priorities)
      setBrandsMap(map)
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setLoading(false)
    }
  }, [modal, reset])

  useEffect(() => {
    fetchBrands()
  }, [fetchBrands])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      setActiveId(null)
      if (!over || active.id === over.id) return

      const oldIndex = orderedIds.indexOf(active.id.toString())
      const newIndex = orderedIds.indexOf(over.id.toString())
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return

      push(arrayMove(orderedIds, oldIndex, newIndex))
    },
    [orderedIds, push]
  )

  const changedIds = useMemo(() => {
    const initialMap = new Map<string, { isMain: boolean; priority?: number }>()
    originalIds.forEach((id) => {
      initialMap.set(
        id,
        getComparableState({ priority: originalPriorityMap[id] })
      )
    })

    const changed = new Set<string>()
    const prioritizedCount = getPrioritizedCountAfterReorder(
      orderedIds,
      originalIds,
      originalPriorityMap
    )

    orderedIds.forEach((id, idx) => {
      const currentState = getComparableState({
        priority: idx < prioritizedCount ? idx + 1 : MAX_PRIORITY,
      })
      const originalState = initialMap.get(id)
      if (
        !originalState ||
        hasComparableStateChanged(currentState, originalState)
      ) {
        changed.add(id)
      }
    })

    return changed
  }, [orderedIds, originalIds, originalPriorityMap])

  const handleSave = useCallback(async () => {
    if (changedIds.size === 0) return
    const updates: Record<string, number> = {}
    const prioritizedCount = getPrioritizedCountAfterReorder(
      orderedIds,
      originalIds,
      originalPriorityMap
    )

    orderedIds.forEach((id, idx) => {
      if (changedIds.has(id)) {
        updates[id] = idx < prioritizedCount ? idx + 1 : MAX_PRIORITY
      }
    })

    const t = toast.loading('Сохранение...')
    setLoading(true)
    try {
      await api.patch('/brands/priorities', { updates })
      await fetchBrands()
      toast.success('Успешно сохранено!', { id: t })
    } catch (e) {
      toast.error('Ошибка при сохранении', { id: t })
      handleApiError(e, modal)
    } finally {
      setLoading(false)
    }
  }, [
    orderedIds,
    changedIds,
    originalIds,
    originalPriorityMap,
    fetchBrands,
    modal,
  ])

  const activeBrand = activeId ? brandsMap[activeId] : null
  const activeIndex = activeId ? orderedIds.indexOf(activeId) : -1
  const prioritizedCount = useMemo(
    () =>
      getPrioritizedCountAfterReorder(
        orderedIds,
        originalIds,
        originalPriorityMap
      ),
    [orderedIds, originalIds, originalPriorityMap]
  )

  return (
    <div
      className={`${cn.container} ${Object.keys(selectedOrganisation).length > 0 ? cn.blueTheme : ''}`}
    >
      {contextHolder}
      <Loader loaderType="modal" isModalOpen={loading} />

      <div className={cn.treeWrapper}>
        <DndContext
          sensors={sensors}
          autoScroll
          onDragStart={(e) => setActiveId(e.active.id.toString())}
          onDragCancel={() => setActiveId(null)}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <div
            className={`${cn.dropZone} ${activeId ? cn.isDraggingGlobal : ''}`}
          >
            {orderedIds.length > 0 ? (
              <SortableContext
                items={orderedIds}
                strategy={verticalListSortingStrategy}
              >
                {orderedIds.map((id, index) => {
                  const brand = brandsMap[id]
                  if (!brand) return null
                  return (
                    <div key={id} className={cn.rootLevel}>
                      <SortablePriorityRow
                        id={id}
                        index={index}
                        name={brand.description}
                        code={brand.code}
                        isChanged={changedIds.has(id)}
                        extra={
                          <span className={cn.rowExtraInline}>
                            {index < prioritizedCount && (
                              <span className={cn.priorityBadge}>
                                Приоритет: {index + 1}
                              </span>
                            )}
                            <Button
                              size="small"
                              onKeyDown={(event) => event.stopPropagation()}
                              onClick={() => setAliasesBrand(brand)}
                            >
                              Синонимы
                            </Button>
                          </span>
                        }
                      />
                    </div>
                  )
                })}
              </SortableContext>
            ) : (
              !loading && (
                <div className={cn.emptyPlaceholder}>Данные отсутствуют</div>
              )
            )}
          </div>

          <DragOverlay
            dropAnimation={{
              duration: 150,
              easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
              sideEffects: defaultDropAnimationSideEffects({
                styles: { active: { opacity: '0.4' } },
              }),
            }}
          >
            {activeBrand ? (
              <PrioritySortRow
                index={activeIndex}
                name={activeBrand.description}
                code={activeBrand.code}
                isChanged={changedIds.has(activeId!)}
                isOverlay
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {aliasesBrand && (
        <BrandAliasesModal
          brand={aliasesBrand}
          canEdit={canEditAliases}
          onClose={() => setAliasesBrand(null)}
        />
      )}

      <PrioritySaveBar
        changeCount={changedIds.size}
        onSave={handleSave}
        onUndo={undo}
        onRedo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
        loading={loading}
      />
    </div>
  )
}

export default BrandsPrioritySort
